import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Briefcase,
  Building2,
  CheckCircle2,
  DollarSign,
  Gift,
  TrendingUp,
  Users,
} from "lucide-react";
import { useState } from "react";
import { useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatCurrency, formatSharesRange, getDepartmentLabel } from "@/lib/constants";
import { orpc, rpcClient } from "@/lib/rpc";

interface OfferDetails {
  id: string;
  salaryBase: string;
  equityShares: number | null;
  signingBonus: string | null;
  performanceBonus: string | null;
  performanceBonusShares: number | null;
  performanceBonusSharesMax: number | null;
  status: string;
  application?: {
    applicant?: {
      fullName: string;
      email: string;
    };
    jobOpening?: {
      title: string;
      department: string;
      jobLevel: string;
    };
  };
}

export default function AcceptOffer() {
  const [, params] = useRoute("/accept-offer/:token");
  const token = params?.token;
  const [accepted, setAccepted] = useState(false);

  const {
    data: offerRaw,
    isLoading,
    error,
  } = useQuery({
    ...orpc.offers.publicOfferStatus.queryOptions({ input: { token: token! } }),
    enabled: !!token,
  });

  // Cast to the shape we need — publicOfferStatus returns minimal info
  const offer = offerRaw as unknown as OfferDetails | undefined;

  const acceptOffer = useMutation({
    mutationFn: async () => {
      // This page doesn't have a verificationToken — it's a simplified accept
      // flow. The main offer-view page handles the full OTP/verification flow.
      return rpcClient.offers.publicAcceptOffer({
        token: token!,
        verificationToken: "",
      });
    },
    onSuccess: () => {
      setAccepted(true);
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="p-12 text-center">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto" />
              <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="p-12 text-center">
            <h2 className="text-2xl font-semibold mb-2">Offer Not Found</h2>
            <p className="text-muted-foreground">This offer link is invalid or has expired.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (offer.status === "accepted" || accepted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="p-12 text-center">
            <div className="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--sand-step))]">
              <CheckCircle2 className="h-8 w-8 text-[hsl(var(--forest-pulse))]" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Welcome to the Team!</h2>
            <p className="text-muted-foreground mb-4">
              Your offer has been accepted. We're excited to have you join us!
            </p>
            <p className="text-sm text-muted-foreground">
              You'll receive onboarding instructions at {offer.application?.applicant?.email} soon.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mb-4 inline-flex items-center justify-center w-14 h-14 rounded-lg bg-primary mx-auto">
            <Building2 className="h-7 w-7 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">
            Congratulations, {offer.application?.applicant?.fullName}!
          </CardTitle>
          <CardDescription className="text-base">
            We're thrilled to offer you the position of{" "}
            <span className="font-medium text-foreground">
              {offer.application?.jobOpening?.title}
            </span>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="rounded-md bg-primary/10 p-2">
                  <Briefcase className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm text-muted-foreground">Position</span>
              </div>
              <p className="font-medium">{offer.application?.jobOpening?.title}</p>
              <p className="text-sm text-muted-foreground">
                {getDepartmentLabel(offer.application?.jobOpening?.department)}
              </p>
            </div>

            <div className="rounded-lg border p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="rounded-md bg-[hsl(var(--sand-step))] p-2">
                  <DollarSign className="h-4 w-4 text-[hsl(var(--forest-pulse))]" />
                </div>
                <span className="text-sm text-muted-foreground">Base Salary</span>
              </div>
              <p className="font-medium text-lg">{formatCurrency(offer.salaryBase)}</p>
              <p className="text-sm text-muted-foreground">per month</p>
            </div>

            {offer.equityShares && (
              <div className="rounded-lg border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-md bg-[hsl(var(--sand-step))] p-2">
                    <Users className="h-4 w-4 text-[hsl(var(--ink-primary))]" />
                  </div>
                  <span className="text-sm text-muted-foreground">Equity</span>
                </div>
                <p className="font-medium">{offer.equityShares.toLocaleString()} shares</p>
              </div>
            )}

            {offer.signingBonus && (
              <div className="rounded-lg border p-4 sm:col-span-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-md bg-[hsl(var(--sand-step))] p-2">
                    <Gift className="h-4 w-4 text-[hsl(var(--amber-state))]" />
                  </div>
                  <span className="text-sm text-muted-foreground">Signing Bonus</span>
                </div>
                <p className="font-medium text-lg">{formatCurrency(offer.signingBonus)}</p>
              </div>
            )}

            {offer.performanceBonus && (
              <div className="rounded-lg border p-4 sm:col-span-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-md bg-[hsl(var(--sand-step))] p-2">
                    <Gift className="h-4 w-4 text-[hsl(var(--amber-state))]" />
                  </div>
                  <span className="text-sm text-muted-foreground">Bônus de Performance</span>
                </div>
                <p className="font-medium text-lg">{formatCurrency(offer.performanceBonus)}</p>
              </div>
            )}
            {offer.performanceBonusShares && (
              <div className="rounded-lg border p-4 sm:col-span-2">
                <div className="flex items-center gap-3 mb-2">
                  <div className="rounded-md bg-[hsl(var(--sand-step))] p-2">
                    <TrendingUp className="h-4 w-4 text-[hsl(var(--ink-primary))]" />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    Bônus de Performance (Shares)
                  </span>
                </div>
                <p className="font-medium text-lg">
                  {formatSharesRange(offer.performanceBonusShares, offer.performanceBonusSharesMax)}
                </p>
              </div>
            )}
          </div>

          <div className="rounded-lg bg-muted/50 p-4 text-center text-sm text-muted-foreground">
            By accepting this offer, you agree to join our team and complete the onboarding process.
          </div>
        </CardContent>

        <CardFooter className="flex-col gap-3 pt-2">
          <Button
            size="lg"
            className="w-full"
            onClick={() => acceptOffer.mutate()}
            disabled={acceptOffer.isPending}
            data-testid="button-accept-offer"
          >
            {acceptOffer.isPending ? "Accepting..." : "Accept Offer"}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Questions? Contact HR at hr@company.com
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
